import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, BoneTypeEnum, ImageRepository, LoaderService, PatientRepository, PlanTypeEnum, SearchArchiveOptions, SearchArchiveResult } from '../core';


/**
 * This service handles archive list.
 */
@Injectable({ providedIn: 'root' })
export class ArchiveService {

	private readonly loaderSrv = inject(LoaderService);
	private readonly imageRepo = inject(ImageRepository);
	private readonly patientRepo = inject(PatientRepository);

	private readonly _searchResult = new BehaviorSubject<SearchArchiveResult>({ items: [], count: 0 });
	private _searchOptions: SearchArchiveOptions = { page: 0, top: 10 };


	/**
	* Get current search result.
	*/
	getSearchResult(): Observable<SearchArchiveResult> {
		return this._searchResult.asObservable();
	}

	/**
	* Set current search result.
	*/
	setSearchResult(result: SearchArchiveResult): void {
		this._searchResult.next(result);
	}

	/**
	* Get current page number option.
	*/
	getPageNumber(): number {
		return this._searchOptions.page;
	}

	/**
	* Get current page size option.
	*/
	getPageSize(): number {
		return this._searchOptions.top;
	}

	/**
	* Update list.
	*/
	search(): void {
		this.loaderSrv.show();
		this.patientRepo.archiveSearch(this._searchOptions).pipe(
			finalize(() => this.loaderSrv.hide()),
			map(res => this.handleApiResponse(res))
		).subscribe(res => this.setSearchResult(res));
	}

	private handleApiResponse<T>(response: ApiResponse<T>) {
		if (response.statusCode == ApiResponseStatusCode.Success) {
			return response.result as T;
		} else {
			throw new Error('Generic error');
		}
	}

	/**
	* Update list by page number.
	*/
	changePageNumber(page: number) {
		this._searchOptions.page = page;
		this.search();
	}

	/**
	* Update list by page size.
	*/
	changePageSize(size: number) {
		this._searchOptions.page = 0;
		this._searchOptions.top = size;
		this.search();
	}

	/**
	 * Update search options.
	 */
	updateFilter(bone?: BoneTypeEnum, type?: PlanTypeEnum): void {
		this._searchOptions.boneType = bone;
		this._searchOptions.planType = type;
	}

	/**
	 * Get view plan pdf.
	 */
	getViewCase(id: string): Observable<string> {
		this.loaderSrv.show();
		return this.imageRepo.getViewCase(id).pipe(
			finalize(() => this.loaderSrv.hide()),
			map(img => URL.createObjectURL(img))
		);
	}

	/**
	 * Get image file.
	 */
	getImage(id: string): Observable<string> {
		this.loaderSrv.show();
		return this.imageRepo.getImage(id).pipe(
			finalize(() => this.loaderSrv.hide()),
			map(img => URL.createObjectURL(img))
		);
	}

	/**
	* Reset search result.
	*/
	resetSearchResult(): void {
		this._searchResult.next({ items: [], count: 0 });
		this._searchOptions = { page: 0, top: 10 };
	}
}